import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, CheckCircle2, AlertCircle, RotateCcw, Save } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useSite } from "@/context/SiteContext";

const PAGES = [
  { key: "site", label: "Global (header / footer)" },
  { key: "about", label: "About" },
  { key: "product-dev", label: "Product Development" },
  { key: "consultancy", label: "Food Consultancy" },
  { key: "franchise", label: "Franchise Solutions" },
  { key: "contact", label: "Contact" },
];

const SCHEMAS = {
  about: [
    { type: "section", label: "Hero" },
    { type: "text", key: "hero_eyebrow", label: "Hero eyebrow" },
    { type: "text", key: "hero_title_pre", label: "Title — start" },
    { type: "text", key: "hero_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "hero_title_post", label: "Title — end" },
    { type: "textarea", key: "hero_intro", label: "Hero paragraph" },

    { type: "section", label: "Vision / Mission / Why" },
    { type: "items", key: "pillars", label: "Pillars",
      fields: [
        { key: "title", label: "Title", type: "text" },
        { key: "body", label: "Body", type: "textarea" },
      ],
    },

    { type: "section", label: "Services section" },
    { type: "text", key: "services_eyebrow", label: "Services eyebrow" },
    { type: "text", key: "services_title_pre", label: "Services title — start" },
    { type: "text", key: "services_title_em", label: "Services title — highlighted phrase" },
    { type: "text", key: "services_title_post", label: "Services title — end" },
    { type: "items", key: "services", label: "Services",
      fields: [
        { key: "title", label: "Title", type: "text" },
        { key: "desc", label: "Description", type: "textarea" },
      ],
    },
    { type: "text", key: "cta_label", label: "CTA button label" },
  ],
  "product-dev": [
    { type: "section", label: "Hero" },
    { type: "text", key: "hero_eyebrow", label: "Hero eyebrow" },
    { type: "text", key: "hero_title_pre", label: "Title — start" },
    { type: "text", key: "hero_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "hero_title_post", label: "Title — end" },
    { type: "textarea", key: "hero_intro", label: "Hero paragraph" },

    { type: "section", label: "New Product Development" },
    { type: "text", key: "new_product_title", label: "Section title" },
    { type: "list", key: "new_product_items", label: "New product categories" },

    { type: "section", label: "Product Improvement" },
    { type: "text", key: "improvement_title", label: "Section title" },
    { type: "list", key: "improvement_items", label: "Improvement items" },

    { type: "section", label: "Inquiry form copy" },
    { type: "text", key: "form_eyebrow", label: "Form eyebrow" },
    { type: "text", key: "form_title_pre", label: "Form title — start" },
    { type: "text", key: "form_title_em", label: "Form title — highlighted phrase" },
    { type: "text", key: "form_title_post", label: "Form title — end" },
    { type: "textarea", key: "form_intro", label: "Form intro paragraph" },
  ],
  consultancy: [
    { type: "section", label: "Hero" },
    { type: "text", key: "hero_eyebrow", label: "Hero eyebrow" },
    { type: "text", key: "hero_title_pre", label: "Title — start" },
    { type: "text", key: "hero_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "hero_title_post", label: "Title — end" },
    { type: "textarea", key: "hero_intro", label: "Hero paragraph" },

    { type: "section", label: "Capabilities section" },
    { type: "text", key: "services_eyebrow", label: "Eyebrow" },
    { type: "text", key: "services_title_pre", label: "Title — start" },
    { type: "text", key: "services_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "services_title_post", label: "Title — end" },
    { type: "list", key: "services", label: "Consultancy capabilities" },

    { type: "section", label: "Form copy" },
    { type: "text", key: "form_eyebrow", label: "Form eyebrow" },
    { type: "text", key: "form_title_pre", label: "Form title — start" },
    { type: "text", key: "form_title_em", label: "Form title — highlighted phrase" },
    { type: "text", key: "form_title_post", label: "Form title — end" },
    { type: "textarea", key: "form_intro", label: "Form intro paragraph" },
  ],
  franchise: [
    { type: "section", label: "Hero" },
    { type: "text", key: "hero_eyebrow", label: "Hero eyebrow" },
    { type: "text", key: "hero_title_pre", label: "Title — start" },
    { type: "text", key: "hero_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "hero_title_post", label: "Title — end" },
    { type: "textarea", key: "hero_intro", label: "Hero paragraph" },

    { type: "section", label: "Process flow" },
    { type: "text", key: "flow_eyebrow", label: "Eyebrow" },
    { type: "text", key: "flow_title_pre", label: "Title — start" },
    { type: "text", key: "flow_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "flow_title_post", label: "Title — end" },
    { type: "items", key: "steps", label: "Steps (max 6 render with icons)",
      fields: [
        { key: "label", label: "Label", type: "text" },
        { key: "sub", label: "Sub-text", type: "text" },
      ],
    },

    { type: "section", label: "Perks section" },
    { type: "text", key: "perks_eyebrow", label: "Eyebrow" },
    { type: "text", key: "perks_title_pre", label: "Title — start" },
    { type: "text", key: "perks_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "perks_title_post", label: "Title — end" },
    { type: "list", key: "perks", label: "Perks" },

    { type: "section", label: "Apply form copy" },
    { type: "text", key: "form_eyebrow", label: "Form eyebrow" },
    { type: "text", key: "form_title_pre", label: "Form title — start" },
    { type: "text", key: "form_title_em", label: "Form title — highlighted phrase" },
    { type: "text", key: "form_title_post", label: "Form title — end" },
    { type: "textarea", key: "form_intro", label: "Form intro paragraph" },
  ],
  contact: [
    { type: "section", label: "Hero" },
    { type: "text", key: "hero_eyebrow", label: "Hero eyebrow" },
    { type: "text", key: "hero_title_pre", label: "Title — start" },
    { type: "text", key: "hero_title_em", label: "Title — highlighted phrase" },
    { type: "text", key: "hero_title_post", label: "Title — end" },
    { type: "textarea", key: "hero_intro", label: "Hero paragraph" },

    { type: "section", label: "Form copy" },
    { type: "text", key: "form_eyebrow", label: "Form eyebrow" },
    { type: "text", key: "form_title", label: "Form heading" },
  ],
};

export default function PagesCMS() {
  const [page, setPage] = useState("site");

  return (
    <div className="p-5 sm:p-8 md:p-12" data-testid="admin-cms">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Website CMS</h1>
        <p className="text-[var(--k-text-2)] mt-2">Edit every public page without code. Changes go live immediately.</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 admin-no-print" data-testid="cms-page-tabs">
        {PAGES.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPage(p.key)}
            data-testid={`cms-tab-${p.key}`}
            className={`whitespace-nowrap px-4 py-2 rounded-full font-mono-label text-[0.6rem] border transition-colors ${
              page === p.key
                ? "border-[var(--k-green)] bg-[var(--k-green)] text-[var(--k-bg)]"
                : "border-[var(--k-line)] text-[var(--k-text-2)] hover:border-[var(--k-green)]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {page === "site" ? <GlobalEditor /> : <PageEditor key={page} pageKey={page} />}
    </div>
  );
}

// ── Global header / footer / contact details ─────────────────────
function GlobalEditor() {
  const { content, refresh } = useSite();
  const [form, setForm] = useState(content);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  useEffect(() => { setForm(content); }, [content]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.put("/admin/content", form);
      await refresh();
      setStatus({ state: "success", msg: "Global content updated." });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <form onSubmit={save} className="organic-card p-6 sm:p-8 space-y-6" data-testid="cms-global-form">
      <Field label="Tagline (used in footer / hero)">
        <input className="kostra-input" value={form.tagline || ""} onChange={set("tagline")} data-testid="cms-input-tagline" />
      </Field>
      <Field label="WhatsApp number">
        <input className="kostra-input" value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="cms-input-whatsapp" />
      </Field>
      <Field label="Public email">
        <input className="kostra-input" type="email" value={form.email || ""} onChange={set("email")} data-testid="cms-input-email" />
      </Field>
      <Field label="Registered office (Noida)">
        <input className="kostra-input" value={form.noida_office || ""} onChange={set("noida_office")} data-testid="cms-input-noida" />
      </Field>
      <Field label="Gurgaon office">
        <input className="kostra-input" value={form.gurgaon_office || ""} onChange={set("gurgaon_office")} data-testid="cms-input-gurgaon" />
      </Field>

      {status.state === "success" && <SuccessBanner msg={status.msg} testId="cms-global-success" />}
      {status.state === "error" && <ErrorBanner msg={status.msg} testId="cms-global-error" />}

      <button type="submit" disabled={status.state === "loading"} className="btn-green" data-testid="cms-global-save">
        <Save className="w-4 h-4" /> {status.state === "loading" ? "Saving…" : "Save Global Content"}
      </button>
    </form>
  );
}

// ── Per-page editor ──────────────────────────────────────────────
function PageEditor({ pageKey }) {
  const schema = useMemo(() => SCHEMAS[pageKey] || [], [pageKey]);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/pages/${pageKey}`);
      setForm(data);
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };
  useEffect(() => { load(); }, [pageKey]);

  const setVal = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.put(`/admin/pages/${pageKey}`, form);
      setStatus({ state: "success", msg: "Page saved. Refresh the public page to see the changes." });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this page to KOSTRA defaults? Your edits will be lost.")) return;
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post(`/admin/pages/${pageKey}/reset`);
      await load();
      setStatus({ state: "success", msg: "Page reset to defaults." });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  if (!form) return <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>;

  return (
    <form onSubmit={save} className="organic-card p-5 sm:p-8 space-y-7" data-testid={`cms-page-form-${pageKey}`}>
      {schema.map((field, i) => {
        if (field.type === "section") {
          return (
            <div key={i} className="pt-2">
              <div className="font-mono-label text-[var(--k-gold)] text-[0.6rem] tracking-widest">{field.label}</div>
              <hr className="border-[var(--k-line)] mt-2" />
            </div>
          );
        }
        const value = form[field.key];
        if (field.type === "text") {
          return (
            <Field key={field.key} label={field.label}>
              <input
                className="kostra-input"
                value={value || ""}
                onChange={(e) => setVal(field.key, e.target.value)}
                data-testid={`cms-${pageKey}-input-${field.key}`}
              />
            </Field>
          );
        }
        if (field.type === "textarea") {
          return (
            <Field key={field.key} label={field.label}>
              <textarea
                className="kostra-input resize-none"
                rows={4}
                value={value || ""}
                onChange={(e) => setVal(field.key, e.target.value)}
                data-testid={`cms-${pageKey}-input-${field.key}`}
              />
            </Field>
          );
        }
        if (field.type === "list") {
          return (
            <ListField
              key={field.key}
              label={field.label}
              values={Array.isArray(value) ? value : []}
              onChange={(next) => setVal(field.key, next)}
              testIdPrefix={`cms-${pageKey}-list-${field.key}`}
            />
          );
        }
        if (field.type === "items") {
          return (
            <ItemsField
              key={field.key}
              label={field.label}
              fields={field.fields}
              values={Array.isArray(value) ? value : []}
              onChange={(next) => setVal(field.key, next)}
              testIdPrefix={`cms-${pageKey}-items-${field.key}`}
            />
          );
        }
        return null;
      })}

      {status.state === "success" && <SuccessBanner msg={status.msg} testId={`cms-${pageKey}-success`} />}
      {status.state === "error" && <ErrorBanner msg={status.msg} testId={`cms-${pageKey}-error`} />}

      <div className="flex flex-wrap gap-3 pt-2">
        <button type="submit" disabled={status.state === "loading"} className="btn-green" data-testid={`cms-${pageKey}-save`}>
          <Save className="w-4 h-4" /> {status.state === "loading" ? "Saving…" : "Save Changes"}
        </button>
        <button type="button" onClick={reset} className="btn-outline-green" data-testid={`cms-${pageKey}-reset`}>
          <RotateCcw className="w-4 h-4" /> Reset to Defaults
        </button>
      </div>
    </form>
  );
}

function ListField({ label, values, onChange, testIdPrefix }) {
  const add = () => onChange([...values, ""]);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <span className="font-mono-label text-[var(--k-muted)] text-[0.6rem] tracking-widest">{label}</span>
        <button type="button" onClick={add} className="btn-outline-green !text-[0.55rem] !py-1.5 !px-3" data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {values.length === 0 && (
          <div className="text-xs text-[var(--k-muted)] italic">No items yet. Click &quot;Add&quot; to create one.</div>
        )}
        {values.map((v, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${testIdPrefix}-row-${i}`}>
            <input
              className="kostra-input flex-1"
              value={v}
              onChange={(e) => update(i, e.target.value)}
              data-testid={`${testIdPrefix}-input-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-500 hover:text-red-700 p-2"
              aria-label="Remove"
              data-testid={`${testIdPrefix}-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemsField({ label, fields, values, onChange, testIdPrefix }) {
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const add = () => onChange([...values, { ...blank }]);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const update = (i, key, v) =>
    onChange(values.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <span className="font-mono-label text-[var(--k-muted)] text-[0.6rem] tracking-widest">{label}</span>
        <button type="button" onClick={add} className="btn-outline-green !text-[0.55rem] !py-1.5 !px-3" data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-4">
        {values.length === 0 && (
          <div className="text-xs text-[var(--k-muted)] italic">No items yet. Click &quot;Add&quot; to create one.</div>
        )}
        {values.map((it, i) => (
          <div key={i} className="border border-[var(--k-line)] rounded-lg p-4 space-y-3 bg-[var(--k-bg)]/40" data-testid={`${testIdPrefix}-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-[var(--k-gold)] text-[0.55rem]">Item {i + 1}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-red-500 hover:text-red-700"
                aria-label="Remove"
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {fields.map((f) => (
                <Field key={f.key} label={f.label} full={f.type === "textarea"}>
                  {f.type === "textarea" ? (
                    <textarea
                      className="kostra-input resize-none"
                      rows={2}
                      value={it[f.key] || ""}
                      onChange={(e) => update(i, f.key, e.target.value)}
                      data-testid={`${testIdPrefix}-input-${i}-${f.key}`}
                    />
                  ) : (
                    <input
                      className="kostra-input"
                      value={it[f.key] || ""}
                      onChange={(e) => update(i, f.key, e.target.value)}
                      data-testid={`${testIdPrefix}-input-${i}-${f.key}`}
                    />
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}

function ErrorBanner({ msg, testId }) {
  return (
    <div className="flex items-start gap-3 border border-red-300 bg-red-50 p-3 rounded" data-testid={testId}>
      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
      <p className="text-[var(--k-text)] text-xs">{msg}</p>
    </div>
  );
}

function SuccessBanner({ msg, testId }) {
  return (
    <div className="flex items-start gap-3 border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)] p-3 rounded" data-testid={testId}>
      <CheckCircle2 className="w-4 h-4 text-[var(--k-green)] mt-0.5" />
      <p className="text-[var(--k-text)] text-xs">{msg}</p>
    </div>
  );
}
